import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Page } from '../../components';
import { SectionCard } from '../../components/SectionCard';
import { Badge, Button, Note } from '../../components/primitives';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/primitives/table';
import { AdminUI } from '../../ui/AdminUI';
import EmptyPage from '../EmptyPage/EmptyPage';
import { PayoutLoader } from './PayoutLoader';
import { formatDate, formatCents } from '../../helpers/formatters';
import { useShopCurrency } from '../../hooks';
import { GetPartnerPayoutDocument } from '../../gql/graphql';

/**
 * A read-only fact, stated as a labelled row rather than a stack of two
 * paragraphs. The payout screens were the last ones still using the old
 * "small grey caption above a value, one per card section" pattern, which
 * turned four facts into four separate bordered boxes.
 */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-right text-sm font-medium text-foreground">{children}</span>
  </div>
);

export const Payout: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const shopCurrency = useShopCurrency();

  const { data, loading, error, fetchMore } = useQuery(GetPartnerPayoutDocument, {
    variables: {
      payoutId: params.payoutId!,
      first: 10,
    },
  });

  if (loading && !data) {
    return (
      <AdminUI>
        <PayoutLoader />
      </AdminUI>
    );
  }

  if (error) {
    return (
      <AdminUI>
        <EmptyPage
          title="This payout didn't load"
          description="The connection dropped or the server is busy. Your money is not affected."
          icon={ExclamationTriangleIcon}
          action={{ label: 'Back to payouts', onAction: () => navigate('/payouts') }}
        />
      </AdminUI>
    );
  }

  const payout = data?.payout;

  if (!payout)
    return (
      <AdminUI>
        <EmptyPage
          title="Payout not found"
          description="It may belong to a different seller account, or the link may be out of date."
          icon={ExclamationTriangleIcon}
          action={{ label: 'Back to payouts', onAction: () => navigate('/payouts') }}
        />
      </AdminUI>
    );

  const orders =
    payout.orders?.edges
      ?.map((edge) => edge?.node)
      .filter((order): order is NonNullable<typeof order> => order != null) || [];
  const hasNextPage = payout.orders?.pageInfo?.hasNextPage || false;
  const endCursor = payout.orders?.pageInfo?.endCursor;

  const handleLoadMore = () => {
    if (!endCursor) return;
    // Append the next page. The old code also setAfter(endCursor), which
    // re-ran the base query with the new cursor and REPLACED the list with
    // page 2 instead of appending.
    fetchMore({
      variables: { after: endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.payout?.orders) return prev;
        return {
          ...fetchMoreResult,
          payout: {
            ...fetchMoreResult.payout,
            orders: {
              ...fetchMoreResult.payout.orders,
              edges: [
                ...(prev.payout?.orders?.edges ?? []),
                ...(fetchMoreResult.payout.orders.edges ?? []),
              ],
            },
          },
        };
      },
    });
  };

  const transaction = payout.transaction;
  const currencyCode =
    transaction.__typename === 'PayoutTransactionStripe'
      ? transaction.to.currencyCode
      : transaction.__typename === 'PayoutTransactionManual'
        ? transaction.to.currencyCode
        : shopCurrency;

  const method =
    transaction.__typename === 'PayoutTransactionStripe'
      ? 'Bank transfer'
      : transaction.__typename === 'PayoutTransactionManual'
        ? transaction.to.type
        : undefined;

  const adjustment = payout.payout?.adjustment
    ? {
        amount: payout.payout.adjustment.amount,
        reason: payout.payout.adjustment.reason || '',
      }
    : undefined;

  const settled =
    payout.payout?.calculatedPayoutAmount ||
    orders.reduce((total: number, order) => total + order.totalPayout, 0) ||
    0;

  const ordersSubtotal = orders.reduce((total: number, order) => total + order.totalPayout, 0);
  // With more pages to load, the rows on screen do not add up to the payout,
  // and a subtotal that silently disagrees with the total below it is worse
  // than one that says which orders it counted.
  const ordersOnThisPage = hasNextPage;

  const paidOn = formatDate(payout.payoutDate, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <AdminUI>
      <Page
        // The number is the page, so it is the title — it was previously
        // "Payout - MP1042" with a shouting PAID chip welded to it.
        title={`Payout #${payout.idAlias}`}
        titleMeta={<Badge variant="success">Paid</Badge>}
        breadcrumbs={[
          { content: 'Payouts', url: '/payouts' },
          { content: payout.idAlias, url: `/payouts/${payout.id}`, current: true },
        ]}
      >
        <div className="flex flex-wrap items-start justify-center gap-5">
          <div className="min-w-[51%] flex-[2_2_30rem] space-y-5">
            <SectionCard
              title="Orders in this payout"
              trailing={`${orders.length} order${orders.length === 1 ? '' : 's'}`}
              testId="payout-orders-section"
              flush
            >
              {/*
                A table, with the sum worked out at the bottom of it.

                This was a list of two-line rows carrying an order number, a
                date and one figure, and it read as sparse — "I don't know if
                there's anything else we can put in there". There is, and the
                query was already fetching it and throwing it away: what the
                customer paid, how many items, and therefore what came off.
                Stating the deduction per row is what makes the total at the
                bottom checkable rather than asserted.

                The rows are one line tall now rather than two, which was the
                other half of the note.
              */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                      <TableHead className="text-right">Order total</TableHead>
                      <TableHead className="text-right">Fees</TableHead>
                      <TableHead className="text-right">Your payout</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => {
                      const units = (order.items ?? []).reduce(
                        (count, item) => count + (item?.quantity ?? 0),
                        0,
                      );
                      const deducted = order.total - order.totalPayout;
                      return (
                        <TableRow key={order.id}>
                          <TableCell className="font-medium">
                            <Link
                              to={`/orders/${order.id}`}
                              className="text-primary hover:underline"
                            >
                              #{order.idAlias}
                            </Link>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(order.orderDate, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">
                            {units}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">
                            {formatCents({ amount: order.total, currencyCode })}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">
                            {deducted > 0
                              ? `−${formatCents({ amount: deducted, currencyCode })}`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {formatCents({ amount: order.totalPayout, currencyCode })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {hasNextPage && (
                <div className="border-t border-border px-5 py-3">
                  <Button variant="outline" size="sm" onClick={handleLoadMore}>
                    Load more
                  </Button>
                </div>
              )}

              {/*
                The arithmetic, where the numbers it is made of are. The
                adjustment used to be a tinted note in the card beside this
                one, so the figure there could not be reconciled with anything:
                "it's not clear to see how everything's calculated".
              */}
              <div className="border-t border-border bg-muted/40 px-5 py-3">
                <div className="ml-auto max-w-sm space-y-1.5">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-sm text-muted-foreground">
                      {ordersOnThisPage
                        ? `Orders on this page (${orders.length})`
                        : `Orders (${orders.length})`}
                    </span>
                    <span className="text-sm tabular-nums text-foreground">
                      {formatCents({ amount: ordersSubtotal, currencyCode })}
                    </span>
                  </div>
                  {adjustment && (
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        Adjustment
                        {adjustment.reason && (
                          <span className="ml-1.5 text-xs">· {adjustment.reason}</span>
                        )}
                      </span>
                      <span className="text-sm tabular-nums text-foreground">
                        {adjustment.amount < 0 ? '−' : '+'}
                        {formatCents({
                          amount: Math.abs(adjustment.amount),
                          currencyCode,
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-between gap-4 border-t border-border pt-2">
                    <span className="text-sm font-semibold text-foreground">Payout total</span>
                    <span className="text-base font-semibold tabular-nums text-foreground">
                      {formatCents({ amount: settled, currencyCode })}
                    </span>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="min-w-0 flex-[1_1_15rem] space-y-5">
            <SectionCard title="Amount" testId="payout-amount-section">
              {/*
                The number, then the sentence that says what it is. The old card
                labelled it "Payout Amount" above the figure and repeated the
                currency code twice on the page without ever saying when it was
                paid or how.
              */}
              <p className="text-2xl font-semibold tabular-nums text-foreground">
                {formatCents({ amount: settled, currencyCode })}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Paid on {paidOn}
                {method ? ` by ${method.toLowerCase()}` : ''}. Amounts are in {currencyCode}.
              </p>
            </SectionCard>

            <SectionCard title="Transaction" testId="payout-transaction-section">
              {transaction.__typename === 'PayoutTransactionStripe' && (
                <>
                  <Fact label="Reference">
                    <span className="font-mono">{transaction.transactionId}</span>
                  </Fact>
                  {/* The bank's name is a name, not a code — uppercasing it in
                      a monospace face made "Bank of America" shout. */}
                  {transaction.to.bankName && (
                    <Fact label="Paid to">
                      {transaction.to.bankName}
                      {transaction.to.last4Digits && (
                        <span className="ml-2 font-mono text-muted-foreground">
                          ••••{transaction.to.last4Digits}
                        </span>
                      )}
                    </Fact>
                  )}
                </>
              )}
              {transaction.__typename === 'PayoutTransactionManual' && (
                <>
                  <Fact label="Reference">
                    <span className="font-mono">{transaction.reference}</span>
                  </Fact>
                  {transaction.to.note && <Fact label="Note">{transaction.to.note}</Fact>}
                </>
              )}
              {transaction.__typename !== 'PayoutTransactionStripe' &&
                transaction.__typename !== 'PayoutTransactionManual' && (
                  <Note>
                    This payout was settled outside the usual channels, so there are no
                    transaction details to show.
                  </Note>
                )}
              <Fact label="Date">{paidOn}</Fact>
            </SectionCard>
          </div>
        </div>
      </Page>
    </AdminUI>
  );
};
